import React, { useCallback, useMemo } from 'react';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useIsAdminGuestSitePreview } from '@apps/guest/shared/hooks/useIsAdminGuestSitePreview';
import { useNativeState } from '@shared/core/native';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import { selectMobileTopBarNavPages } from './selectMobileTopBarNavPages';
import { StickyNavVariant } from './StickyNavVariant';
import { useGuestSiteNavUtm } from '@apps/guest/packages/layout-engine/common/utils/useGuestSiteNavUtm';
import type { WritableUtmParameters } from '@shared/utils/utmParameters';
import type { MobileTopBarNavProps } from './MobileTopBarNav.types';

export const MobileTopBarNav: React.FC<MobileTopBarNavProps> = ({ eventHandle, pages }) => {
  const isMobile = useIsMobileScreen();
  // Admin preview (joy's own preview iframe) — distinct from other iframe contexts like Carbon
  // embedding or custom domains, where this is a real guest visit and the nav must stay fully
  // clickable. Bare isInIframe() would wrongly treat those as preview.
  const isAdminPreview = useIsAdminGuestSitePreview();
  // Skip in native webviews: the native app supplies its own chrome, and on the native
  // accommodations page the web header (and the EventMenu the hamburger opens) is intentionally
  // hidden — so the bar's hamburger would have nothing to open. This nav is mobile-web only.
  const { isNative } = useNativeState();
  // isNative reads localStorage, so SSR always sees false and renders the nav even for the
  // native webview. Hydrating that markup against a null first client render made React adopt
  // the nav's DOM node (opacity-0 GlassmorphicBar) as the page wrapper, blanking the whole page.
  // Ignore isNative until hydration so the first client render matches SSR (keeping the nav in
  // server HTML for crawlers); the native webview then unmounts it, invisibly (bar is opacity-0).
  const isHydrated = useIsMounted();
  const isEligible = isMobile && (!isHydrated || !isNative);
  const eventPageRoute = useEventPageRoute(eventHandle, pages);
  const resolveNavUtm = useGuestSiteNavUtm('mobile_top_bar_nav');
  const telemetry = useGuestSiteTelemetry();

  const visiblePages = useMemo(() => selectMobileTopBarNavPages(pages), [pages]);

  const onLinkClick = useCallback(
    (pageSlug: string, utm?: WritableUtmParameters) => {
      // Don't pollute guest-usage dashboards with admin-preview interactions.
      if (isAdminPreview) return;
      telemetry.mobileTopBarNavLinkClicked(pageSlug, utm);
    },
    [telemetry, isAdminPreview],
  );

  const onMenuClick = useCallback(() => {
    if (isAdminPreview) return;
    telemetry.mobileTopBarNavMenuOpened();
  }, [telemetry, isAdminPreview]);

  if (!isEligible) return null;
  if (visiblePages.length === 0) return null;

  return (
    <StickyNavVariant
      pages={visiblePages}
      onLinkClick={onLinkClick}
      onMenuClick={onMenuClick}
      eventPageRoute={eventPageRoute}
      resolveNavUtm={resolveNavUtm}
      isAdminPreview={isAdminPreview}
    />
  );
};

MobileTopBarNav.displayName = 'MobileTopBarNav';
